import axios from "axios";

/**
 * Postiz API wrapper for Infinium
 * Handles all communication with Postiz backend
 */

// Get base URL from config or environment
const getPostizApiUrl = () => {
  // console.log('process.env.REACT_APP_POSTIZ_API_URL:', process.env.REACT_APP_POSTIZ_API_URL);
  // console.log('process.env:', process.env);

  // Allow explicit override for local development
  if (typeof process !== 'undefined' && process.env.REACT_APP_POSTIZ_API_URL) {
    return process.env.REACT_APP_POSTIZ_API_URL;
  }

  // Dynamically derive the Postiz backend URL from the current origin domain.
  // Frontend served from *.helloonline.in  -> postiz-backend.helloonline.in
  // Frontend served from *.igr-consulting.com -> postiz-backend.igr-consulting.com
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname;
    if (hostname.includes('helloonline.in')) {
      return 'https://postiz-backend.helloonline.in/api';
    }
    if (hostname.includes('igr-consulting.com')) {
      return 'https://postiz-backend.igr-consulting.com/api';
    }
  }

  // Fallback for safety
  return 'https://postiz-backend.igr-consulting.com/api';
};

/**
 * Create axios instance for Postiz API calls
 * Token is added by request interceptor
 */
/**
 * Create axios instance for Postiz API calls
 * Token is added by request interceptor
 * baseURL is set at request time to ensure fresh env vars
 */
const createPostizClient = (token) => {
  const client = axios.create({
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Set baseURL at request time (not creation time) so getPostizApiUrl() is always fresh
  client.interceptors.request.use((config) => {
    config.baseURL = getPostizApiUrl();

    const orgId = sessionStorage.getItem('postiz_org_id');

    if (token) {
      config.headers.auth = token;
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (orgId) {
      config.headers.showorg = orgId;
    }

    return config;
  });

  return client;
};

const normalizePostItem = (item = {}) => {
  const integrationRaw = item.integration || item.n || null;

  return ({
  id: item.id || item.i,
  content: item.content || item.c || "",
  publishDate: item.publishDate || item.d || null,
  releaseURL: item.releaseURL || item.u || null,
  releaseId: item.releaseId || item.ri || null,
  state: item.state || item.s || null,
  group: item.group || item.g || null,
  actualDate: item.actualDate || item.ad || null,
  errorMessage: item.errorMessage || item.em || null,
  integration: integrationRaw
    ? {
        id: integrationRaw.id || integrationRaw.i,
        providerIdentifier:
          integrationRaw.providerIdentifier || integrationRaw.pi || "unknown",
        name: integrationRaw.name || integrationRaw.n || "Unknown",
        picture: integrationRaw.picture || integrationRaw.p || null,
      }
    : null,
  });
};

const normalizePostsListResponse = (data = {}) => {
  const rawPosts = data.posts || data.p || [];
  return {
    posts: rawPosts.map(normalizePostItem),
    total: data.total ?? data.t ?? rawPosts.length,
    page: data.page ?? data.pg ?? 0,
    limit: data.limit ?? data.l ?? rawPosts.length,
    hasMore: data.hasMore ?? data.hm ?? false,
  };
};

const isPinterestIntegration = (integration) =>
  integration?.platform === 'pinterest' || integration?.identifier === 'pinterest';

const loadImageSize = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });

/**
 * Check Pinterest's media rules before sending the post, so the user gets the
 * reason right away instead of a failed post later.
 * Returns an error message, or null when the media is fine.
 */
const validatePinterestMedia = async (images, integrationIds, integrations) => {
  const targetsPinterest = (integrationIds || []).some((id) =>
    isPinterestIntegration((integrations || []).find((i) => i.id === id))
  );
  if (!targetsPinterest) return null;

  if (images.length === 0) {
    return 'Pinterest requires at least one image or video.';
  }

  const hasVideo = images.some((m) => /\.(mp4|mov|webm)(\?|$)/i.test(m.path || ''));
  if (hasVideo || images.length === 1) return null;

  if (images.length > 5) {
    return 'Pinterest allows at most 5 images in a multi-image Pin.';
  }

  const sizes = await Promise.all(images.map((m) => loadImageSize(m.path)));
  // If an image can't be loaded here, let Pinterest decide rather than blocking the post
  if (sizes.some((s) => !s || !s.width || !s.height)) return null;

  const firstRatio = sizes[0].width / sizes[0].height;
  const sameRatio = sizes.every((s) => Math.abs(s.width / s.height - firstRatio) / firstRatio < 0.01);
  if (!sameRatio) {
    return 'All images in a multi-image Pinterest Pin must have the same shape (width/height ratio). Crop them to the same ratio, e.g. 2:3 or 1:1, and try again.';
  }

  return null;
};

const mapStateToStatus = (state) => {
  const normalized = String(state || '').toUpperCase();
  if (normalized === 'PUBLISHED') return 'published';
  if (normalized === 'QUEUE' || normalized === 'DRAFT') return 'scheduled';
  if (normalized === 'ERROR') return 'failed';
  return 'scheduled';
};

export const postizAPI = {
  /**
   * Sync a vCX user to Postiz (or retrieve existing Postiz account)
   * Called when user signs up for/upgrades to CMO SERVICE
   */
syncUser: async (userData) => {
  try {
    const postizUrl = getPostizApiUrl();
    const fullUrl = `${postizUrl}/auth/infinium-sync`;
    const response = await axios.post(fullUrl, {
      email: userData.email,
      name: userData.name,
      organizationName: "Demo_Organization", // ← placeholder, Postiz backend will handle fallback logic
      externalUserId: userData.externalUserId,
    });

    // Postiz returns { token, userId, organizationId } directly
    if (response.data && response.data.token) {
      return {
        success: true,
        data: {
          token: response.data.token,
          postizUserId: response.data.postizUserId,   // ← exact field name from auth.service.ts
          organizationId: response.data.organizationId,
          apiKey: response.data.apiKey,
        },
      };
    }
    return {
      success: false,
      error: 'No token returned from Postiz sync',
    };
  } catch (error) {
    console.error('Error syncing user to Postiz:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message,
    };
  }
},

  /**
   * Get Postiz user status (lazy-load check)
   */
  checkStatus: async (vCxUserId, email) => {
    try {
      const postizUrl = getPostizApiUrl();
      const fullUrl = `${postizUrl}/auth/infinium-status`;
      const response = await axios.get(fullUrl, {
        params: {
          externalUserId: vCxUserId,
          email,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error checking Postiz status:', error);
      return {
        success: false,
        exists: false,
      };
    }
  },

  /**
   * Get list of connected social integrations (platforms user has connected)
   */
  getConnectors: async (token) => {
    try {
      const client = createPostizClient(token);
      const response = await client.get('/integrations/list');

      return {
        success: true,
        data: (response.data.integrations || []).map(item => ({
          id: item.id,
          platform: item.identifier || item.providerIdentifier || item.token,  // Fixed: use identifier instead of token
          name: item.name,             // display name / account handle
          display: item.display || null,
          customer: item.customer || null,
          picture: item.picture || null,
          disabled: item.disabled || false,
          refreshNeeded: item.refreshNeeded || false,
          inBetweenSteps: item.inBetweenSteps || false,
        })),
      };
    } catch (error) {
      console.error('Error fetching connectors:', error);
      return {
        success: false,
        data: [],
        error: error.message,
      };
    }
  },

  /**
   * Call a provider-specific custom function (e.g. Pinterest 'boards')
   */
  getProviderFunction: async (token, integrationId, funcName, customData = null) => {
    try {
      const client = createPostizClient(token);
      const response = await client.post('/integrations/function', {
        name: funcName,
        id: integrationId,
        data: customData,
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error calling provider function "${funcName}":`, error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message,
      };
    }
  },

  /**
   * Get OAuth URL for connecting a social platform
   * Optionally accepts returnUrl for redirect after OAuth completion
   */
  getOAuthUrl: async (token, platformId, returnUrl = null) => {
    try {
      const client = createPostizClient(token);
      // Postiz endpoint: GET /integrations/social/:integration?returnUrl=...
      // It returns { url, codeVerifier, state }
      const params = new URLSearchParams();
      if (returnUrl) {
        params.append('returnUrl', returnUrl);
      }
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await client.get(`/integrations/social/${platformId}${queryString}`);

      return {
        success: true,
        data: {
          oauthUrl: response.data.url,   // shape PMConnectors.js expects
        },
      };
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  /**
   * Upload a media file to Postiz storage.
   * Returns { success, id, url } — id + url are used to build MediaDto[]
   */
  uploadMedia: async (token, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const postizUrl = getPostizApiUrl();
      const orgId = sessionStorage.getItem('postiz_org_id');

      const response = await axios.post(
        `${postizUrl}/media/upload-simple`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            auth: token,
            Authorization: `Bearer ${token}`,
            ...(orgId ? { showorg: orgId } : {}),
          },
        }
      );

      // Postiz returns { id, name, path, originalName, thumbnail, alt }
      const url = response.data?.path || response.data?.url || response.data?.location;
      const id = response.data?.id;
      if (!url) {
        return { success: false, error: 'No URL returned from media upload' };
      }
      return { success: true, id, url };
    } catch (error) {
      console.error('Error uploading media:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  /**
   * Schedule (or immediately publish) a post via Postiz.
   * postData: { text, images: [{id, path}], integrationIds, publishedAt, postNow }
   *
   * Postiz CreatePostDto shape:
   *   { type, shortLink, date, tags, posts: [{ integration: {id}, value: [{content, image}], settings }] }
   */
  schedulePost: async (token, postData) => {
    try {
      const client = createPostizClient(token);

      const images = (postData.images || []).filter((m) => m.id && m.path);
      const type = postData.postNow ? 'now' : 'schedule';

      const mediaError = await validatePinterestMedia(images, postData.integrationIds, postData.integrations);
      if (mediaError) return { success: false, error: mediaError };

      // One Post object per integration ID
      const posts = (postData.integrationIds || []).map((integrationId) => {
        const integration = (postData.integrations || []).find(i => i.id === integrationId);
        const isInstagram = integration?.platform === 'instagram' || integration?.platform === 'instagram-standalone'
          || integration?.identifier === 'instagram' || integration?.identifier === 'instagram-standalone';
        const isPinterest = integration?.platform === 'pinterest' || integration?.identifier === 'pinterest';
        return {
          integration: { id: integrationId },
          value: [
            {
              content: postData.text || '',
              image: images,
            },
          ],
          settings: isInstagram
            ? { post_type: 'post' }
            : isPinterest
              ? { board: (postData.pinterestBoardIds || {})[integrationId] || '' }
              : {},
        };
      });

      const response = await client.post('/posts/', {
        type,
        shortLink: false,
        date: postData.publishedAt || new Date().toISOString(),
        tags: [],
        posts,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error scheduling post:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  /**
   * Publish a post immediately to one or more social platforms.
   * Convenience wrapper around schedulePost with type='now'.
   * postData: { text, images: [{id, path}], integrationIds }
   */
  publishPost: async (token, postData) => {
    try {
      const client = createPostizClient(token);

      const images = (postData.images || []).filter((m) => m.id && m.path);

      const mediaError = await validatePinterestMedia(images, postData.integrationIds, postData.integrations);
      if (mediaError) return { success: false, error: mediaError };

      // One Post object per integration ID
      const posts = (postData.integrationIds || []).map((integrationId) => {
        const integration = (postData.integrations || []).find(i => i.id === integrationId);
        const isInstagram = integration?.platform === 'instagram' || integration?.platform === 'instagram-standalone'
          || integration?.identifier === 'instagram' || integration?.identifier === 'instagram-standalone';
        const isPinterest = integration?.platform === 'pinterest' || integration?.identifier === 'pinterest';
        return {
          integration: { id: integrationId },
          value: [
            {
              content: postData.text || '',
              image: images,
            },
          ],
          settings: isInstagram
            ? { post_type: 'post' }
            : isPinterest
              ? { board: (postData.pinterestBoardIds || {})[integrationId] || '' }
              : {},
        };
      });

      const response = await client.post('/posts/', {
        type: 'now',
        shortLink: false,
        date: new Date().toISOString(),
        tags: [],
        posts,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error publishing post:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  /**
   * Get posts list (published, queued, etc.) with optional pagination.
   */
  getPostsList: async (token, options = {}) => {
    try {
      const client = createPostizClient(token);
      const requestedLimit = Number(options.limit ?? 50);
      const safeLimit = Math.min(100, Math.max(1, Number.isFinite(requestedLimit) ? requestedLimit : 50));
      const requestedPage = Number(options.page ?? 0);
      const safePage = Math.max(0, Number.isFinite(requestedPage) ? requestedPage : 0);
      const response = await client.get('/posts/list', {
        params: {
          page: safePage,
          limit: safeLimit,
          ...(options.customer ? { customer: options.customer } : {}),
        },
      });

      return {
        success: true,
        data: normalizePostsListResponse(response.data),
      };
    } catch (error) {
      console.error('Error fetching posts list:', error);
      return {
        success: false,
        data: { posts: [], total: 0, page: 0, limit: 0, hasMore: false },
        error: error.response?.data?.message || error.message,
      };
    }
  },

  /**
   * Get posts within a date range (includes past published posts and future scheduled posts).
   */
  getPostsByDateRange: async (token, options = {}) => {
    try {
      const client = createPostizClient(token);
      const now = new Date();
      const startDate = options.startDate || new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const endDate = options.endDate || new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();

      const response = await client.get('/posts/', {
        params: {
          startDate,
          endDate,
          ...(options.customer ? { customer: options.customer } : {}),
        },
      });

      return {
        success: true,
        data: normalizePostsListResponse(response.data),
      };
    } catch (error) {
      console.error('Error fetching posts by date range:', error);
      return {
        success: false,
        data: { posts: [], total: 0, page: 0, limit: 0, hasMore: false },
        error: error.response?.data?.message || error.message,
      };
    }
  },

  /**
   * Get scheduled posts for CMO SERVICE user
   */
  getScheduledPosts: async (token, filters = {}) => {
    try {
      const now = new Date();
      const result = await postizAPI.getPostsByDateRange(token, {
        startDate:
          filters.startDate ||
          new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString(),
        endDate:
          filters.endDate ||
          new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString(),
      });

      if (!result.success) {
        return {
          success: false,
          data: [],
          error: result.error || 'Failed to fetch scheduled posts',
        };
      }

      const posts = (result.data?.posts || []).map((post) => {
        const platform = post.integration?.providerIdentifier || 'unknown';

        // Extract the first image URL from the post content if available.
        // Postiz stores attached media in post.image (array or single object).
        const rawImages = post.image || post.images || [];
        const imagesArr = Array.isArray(rawImages) ? rawImages : (rawImages ? [rawImages] : []);
        const firstImageUrl = imagesArr[0]?.path || imagesArr[0]?.url || imagesArr[0] || null;

        return {
          id: post.id,
          group: post.group || null,           // required for correct deletion
          title: post.content ? String(post.content).slice(0, 80) : 'Scheduled post',
          body: post.content || '',
          caption: post.content || '',
          status: post.releaseURL ? 'published' : mapStateToStatus(post.state),
          scheduledDate: post.publishDate,
          createdAt: post.actualDate || post.publishDate,
          platforms: [platform],
          platform,
          releaseURL: post.releaseURL || null,
          releaseId: post.releaseId || null,
          imageUrl: firstImageUrl,             // first media attachment URL for edit modal preview
          state: post.state || 'QUEUE',
          errorMessage: post.errorMessage || null,   // why Postiz failed to publish (state ERROR)
        };
      }).sort((a, b) => new Date(a.scheduledDate || a.createdAt) - new Date(b.scheduledDate || b.createdAt));

      return {
        success: true,
        data: posts,
      };
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
      return {
        success: false,
        data: [],
        error: error.message,
      };
    }
  },

  /**
   * Get post analytics
   */
  getPostAnalytics: async (token, postId, days = 30) => {
    try {
      const client = createPostizClient(token);
      const response = await client.get(`/analytics/post/${postId}`, {
        params: {
          date: days,
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching post analytics:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  /**
   * Get aggregated analytics for all platforms
   */
  getAggregateAnalytics: async (token, dateRange = {}) => {
    try {
      const client = createPostizClient(token);
      const params = new URLSearchParams({
        source: 'infinium-pm',
        ...dateRange, // { startDate, endDate }
      });

      const response = await client.get(`/analytics?${params}`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  /**
   * Fetch the full post group by group ID.
   * Returns the complete post data including image attachments.
   * Used to populate the edit modal with existing media.
   *
   * API response shape (from posts.service.ts getPostsByGroup):
   * {
   *   group: string,
   *   posts: [ { ...post, image: [{ path, url, type, ... }] } ],
   *   integrationPicture: string,
   *   integration: string,
   *   settings: {}
   * }
   * Note: image is already a parsed array (not a JSON string) at this point,
   * and each image object has both `url` (absolute) and `path` fields.
   */
  getPostGroup: async (token, groupId) => {
  try {
    const client = createPostizClient(token);
    const response = await client.get(`/posts/group/${groupId}`);
    const data = response.data;

    const posts = data?.posts || [];
    const firstPost = posts[0] || null;

    if (!firstPost) {
      return { success: false, error: 'Post group not found' };
    }

    // image is already a parsed array of { path, url, type } objects
    const imageArr = Array.isArray(firstPost.image) ? firstPost.image : [];
    const images = imageArr.map((img) => ({
      url: img.url || img.path || null,
      path: img.path || img.url || null,
    }));
    const imageUrl = images[0]?.url || images[0]?.path || null;

    return {
      success: true,
      data: {
        id: firstPost.id,
        group: data.group || firstPost.group,
        content: firstPost.content || '',
        imageUrl,      // kept for backward compat (edit modal preview)
        images,         // NEW: full array for carousels
        publishDate: firstPost.publishDate,
        state: firstPost.state,
        integration: firstPost.integration || null,
        allPosts: posts,
      },
    };
  } catch (error) {
    console.error('Error fetching post group:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message,
    };
  }
},

  /**
   * Reschedule an existing post — changes its date only.
   * Uses PUT /posts/:id/date  (action='update' keeps the content unchanged)
   */
  updatePostDate: async (token, postId, newDate) => {
    try {
      const client = createPostizClient(token);
      const response = await client.put(`/posts/${postId}/date`, {
        date: newDate,
        action: 'update',
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating post date:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  /**
   * Delete a scheduled post by its group ID (removes the whole post group)
   */
  deletePost: async (token, postId) => {
    try {
      const client = createPostizClient(token);
      // Postiz DELETE /posts/:id removes the post group
      const response = await client.delete(`/posts/${postId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error deleting post:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },

  /**
   * Disconnect a social platform
   */
  disconnectIntegration: async (token, integrationId) => {
    try {
      const client = createPostizClient(token);
      // Authenticated API expects DELETE /integrations/ with { id } in request body
      const response = await client.delete('/integrations/', {
        data: { id: integrationId },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },
};

/**
 * Analytics API wrapper
 */
export const postizAnalyticsAPI = {
  /**
   * Get analytics for a specific timeframe
   */
  getAnalytics: async (token, options = {}) => {
    try {
      const client = createPostizClient(token);
      const { dateRange, platforms, limit = 30 } = options;

      const response = await client.get('/analytics', {
        params: {
          startDate: dateRange?.start,
          endDate: dateRange?.end,
          platforms: platforms?.join(','),
          limit,
          source: 'infinium-pm',
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  /**
   * Get top performing posts
   */
  getTopPosts: async (token, limit = 5) => {
    try {
      const client = createPostizClient(token);
      const response = await client.get('/posts/top', {
        params: {
          limit,
          source: 'infinium-pm',
        },
      });

      return {
        success: true,
        data: response.data.posts || [],
      };
    } catch (error) {
      console.error('Error fetching top posts:', error);
      return {
        success: false,
        data: [],
        error: error.message,
      };
    }
  },

  /**
   * Get engagement metrics by platform
   */
  getPlatformMetrics: async (token) => {
    try {
      const client = createPostizClient(token);
      const response = await client.get('/analytics/by-platform', {
        params: {
          source: 'infinium-pm',
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching platform metrics:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },
};

export default postizAPI;